/**
 * Kanban Board Component
 *
 * Implements:
 * - REQ-012: Kanban Board Rendering
 *
 * Displays tasks from plan reasoning results.
 */

import { useMemo } from "react";
import TaskCard from "./TaskCard";
import type { Task } from "../lib/usePlanReasoning";

interface BoardProps {
  tasks: Task[];
  isLoading?: boolean;
  onTaskClick?: (taskId: string) => void;
  agentFilter?: "all" | "C" | "S" | "A";
  selectedTask?: string | null;
}

// Skeleton card with shimmer effect
function SkeletonCard() {
  return (
    <div className="card bg-base-200 animate-pulse">
      <div className="card-body p-2 sm:p-3 space-y-2">
        <div className="flex justify-between items-start">
          <div className="h-4 w-16 bg-base-300 rounded shimmer"></div>
          <div className="h-4 w-6 bg-base-300 rounded shimmer"></div>
        </div>
        <div className="h-3 w-20 bg-base-300 rounded shimmer"></div>
      </div>
    </div>
  );
}

export default function Board({ tasks, isLoading, onTaskClick, agentFilter = "all", selectedTask }: BoardProps) {
  // Filter tasks by agent type
  const filteredTasks = useMemo(() => {
    if (agentFilter === "all") return tasks;
    return tasks.filter((t) => t.agentType === agentFilter);
  }, [tasks, agentFilter]);

  const columns = [
    { id: "backlog", title: "Backlog", colorClass: "text-base-content/40" },
    { id: "ready", title: "Ready", colorClass: "text-primary" },
    { id: "in_progress", title: "In Progress", colorClass: "text-secondary" },
    { id: "blocked", title: "Blocked", colorClass: "text-warning" },
    { id: "done", title: "Done", colorClass: "text-success" },
  ] as const;

  return (
    <div className="relative">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 sm:gap-3 h-full">
        {columns.map((column, colIndex) => {
          const columnTasks = filteredTasks.filter((t) => t.status === column.id);
          // Show skeletons while WASM is reasoning
          const skeletonCounts = [3, 2, 1, 1, 0]; // backlog, ready, in_progress, blocked, done

          return (
            <div key={column.id}>
              <div className={`text-xs font-medium uppercase mb-2 ${column.colorClass}`}>
                {column.title}
              </div>
              <div className="space-y-2">
                {isLoading ? (
                  // Show skeleton cards while reasoning
                  Array.from({ length: skeletonCounts[colIndex] }).map((_, i) => (
                    <SkeletonCard key={`skeleton-${column.id}-${i}`} />
                  ))
                ) : (
                  columnTasks.map((task) => (
                    <TaskCard
                      key={task.id}
                      task={task}
                      onClick={() => onTaskClick?.(task.id)}
                      isSelected={task.id === selectedTask}
                    />
                  ))
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
